// this file sets up our connection to supabase (our auth + database backend)
// and shares one single client that the whole app can import and reuse
import { createClient } from '@supabase/supabase-js';

// these two come from environment variables so we don't hardcode secrets in the code
// the url is where our supabase project lives
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
// the anon key is the public "safe to ship" key for the browser
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

// make the actual client and export it - every other file grabs this one instance
export const supabase = createClient(supabaseUrl, supabaseAnonKey);

// our chrome extension's id in the web store. the extension names this site in
// its manifest under externally_connectable, which is what lets the page below
// message it directly. that isn't a permission, so shipping it doesn't make
// chrome disable the extension until users re-approve it - which is exactly what
// adding a new site to host_permissions would do.
const EXTENSION_ID = 'bccghhjlejbkimijdkdfoboamcjgpefg';

// a copy of the extension loaded unpacked for development gets its own random id
// instead of the store one above, so nothing we send ever reaches it. rather than
// rebuild the site for each machine, it can be named from the browser console:
//   localStorage.setItem('gptdisguise_dev_extension_id', '<id from chrome://extensions>')
// sending to an id that isn't installed just fails quietly, so this costs nothing
// in production.
function extensionIds() {
  const ids = [EXTENSION_ID];
  try {
    const dev = localStorage.getItem('gptdisguise_dev_extension_id');
    if (dev && dev !== EXTENSION_ID) ids.push(dev);
  } catch {
    // localStorage can be blocked entirely; the store id alone is fine
  }
  return ids;
}

// hand the login straight to the extension. this replaces the old trick of the
// extension reading our localStorage: that only worked on the domain listed in
// its manifest, and this site has moved.
export function sendSessionToExtension(session) {
  // chrome.runtime only exists here when an extension that accepts messages from
  // this site is installed. everything else (firefox, safari, plain browsers with
  // no extension) just skips this quietly.
  if (typeof chrome === 'undefined' || !chrome.runtime || !chrome.runtime.sendMessage) return;

  // logged in -> hand over the tokens. logged out -> tell it to forget them.
  const message = session?.access_token
    ? {
        type: 'gptdisguise_login',
        jwt: session.access_token,
        refreshToken: session.refresh_token,
        expiry: session.expires_at,
      }
    : { type: 'gptdisguise_logout' };

  for (const id of extensionIds()) {
    try {
      chrome.runtime.sendMessage(id, message, () => {
        // touching lastError stops chrome logging a scary "unchecked runtime.lastError"
        // in the console when the extension isn't installed, or is an older version
        // that doesn't listen yet. there's nothing for us to do about either.
        void chrome.runtime.lastError;
      });
    } catch {
      // no extension, or it refused the message - the site works fine without it
    }
  }
}

// the website logs you in, and the extension needs to know you're logged in too.
// so we stash your login token in localStorage, where the extension can read it
// back off the page.
export function syncJWTForExtension(session) {
  // if there's a session with a token, you're logged in - save the goods
  if (session?.access_token) {
    // the main token that proves who you are
    localStorage.setItem('gptdisguise_jwt', session.access_token);
    // the refresh token lets the extension get a fresh token when this one expires
    localStorage.setItem('gptdisguise_refresh_token', session.refresh_token);
    // when the token goes stale, so the extension knows when to refresh
    localStorage.setItem('gptdisguise_jwt_expiry', session.expires_at);
  } else {
    // no session means you logged out - wipe everything so the extension knows too
    localStorage.removeItem('gptdisguise_jwt');
    localStorage.removeItem('gptdisguise_refresh_token');
    localStorage.removeItem('gptdisguise_jwt_expiry');
  }
}

// this fires automatically whenever you log in, log out, or your token refreshes,
// so localStorage stays up to date without us having to think about it
supabase.auth.onAuthStateChange((event, session) => {
  syncJWTForExtension(session);
  // and push it to the extension too, for versions that listen for it
  sendSessionToExtension(session);
});
